import { hex, type Color } from "../../common/color.js";
import { insets, type Rect } from "../../common/geometry.js";
import type { RenderContext } from "../render/RenderContext.js";
import {
  Theme,
  type CaptionButton,
  type ProgressVisual,
  type SpinnerVisual,
  type SplitterVisualState,
  type TabVisualState,
  type ScrollBarParts,
  type ThemeMetrics,
  type ThemePalette,
  type VisualState,
  type WindowVisualState,
} from "./Theme.js";

const UI_FONT = "Tahoma, 'MS Sans Serif', 'Segoe UI', sans-serif";
const MONO_FONT = "'Lucida Console', 'Courier New', monospace";

const METRICS: ThemeMetrics = {
  titleBarHeight: 18,
  windowBorder: 4,
  windowCornerRadius: 0,
  resizeGripSize: 4,
  captionButtonWidth: 16,
  windowPadding: insets(0),

  controlCornerRadius: 0,
  controlHeight: 21,
  buttonPadding: insets(10, 3),
  buttonMinWidth: 75,
  focusRingWidth: 1,
  focusRingInset: 3,

  textBoxPadding: insets(4, 3),

  checkBoxSize: 13,
  radioSize: 13,
  checkLabelGap: 5,

  splitterSize: 4,
  spinnerSize: 24,

  // Win95 trackbar: a tall pointed slug on a thin sunken channel.
  sliderThumbSize: 20,
  sliderTrackThickness: 4,
  spinButtonWidth: 17,

  scrollBarSize: 16,
  scrollBarMinThumb: 16,
  scrollBarArrows: true,

  listItemHeight: 17,
  listPadding: insets(2),
  headerHeight: 18,
  columnPadding: 5,

  dialogPadding: 12,
  dialogButtonGap: 6,

  font: { family: UI_FONT, size: 11 },
  captionFont: { family: UI_FONT, size: 11, weight: 700 },
  monoFont: { family: MONO_FONT, size: 11 },
};

const FACE = hex("#c0c0c0");
const HIGHLIGHT = hex("#ffffff");
const LIGHT = hex("#dfdfdf");
const SHADOW = hex("#808080");
const DARK_SHADOW = hex("#0a0a0a");
/** Draw-path colors, parsed once rather than on every call. */
const BLACK = hex("#000000");
const TOOLTIP_BG = hex("#ffffe1");

const PALETTE: ThemePalette = {
  desktop: hex("#008080"),

  windowBg: FACE,
  windowBorderActive: DARK_SHADOW,
  windowBorderInactive: DARK_SHADOW,
  titleBarActive: hex("#000080"),
  titleBarInactive: hex("#808080"),
  titleTextActive: hex("#ffffff"),
  titleTextInactive: hex("#c0c0c0"),

  controlBg: FACE,
  controlBgHover: FACE,
  controlBgPressed: FACE,
  controlBgDisabled: FACE,
  controlBorder: SHADOW,
  controlBorderHover: SHADOW,

  fieldBg: hex("#ffffff"),
  fieldBgDisabled: FACE,
  fieldBorder: SHADOW,
  fieldBorderFocused: SHADOW,

  text: hex("#000000"),
  textDisabled: SHADOW,
  textSubtle: hex("#404040"),
  buttonText: hex("#000000"),
  fieldText: hex("#000000"),
  placeholderText: hex("#404040"),
  headingText: hex("#000000"),

  accent: hex("#000080"),
  accentHover: hex("#0000a0"),
  accentPressed: hex("#000060"),
  accentText: hex("#ffffff"),

  selectionBg: hex("#000080"),
  selectionText: hex("#ffffff"),
  selectionInactiveBg: hex("#808080"),
  textSelectionBg: hex("#000080"),

  scrollTrack: hex("#dfdfdf"),
  scrollThumb: FACE,
  scrollThumbHover: FACE,
  scrollThumbPressed: FACE,

  headerBg: FACE,
  headerBgHover: FACE,
  headerText: hex("#000000"),
  gridLine: SHADOW,

  focusRing: hex("#000000"),
  shadow: hex("#00000000"),

  bevelHighlight: HIGHLIGHT,
  bevelLight: LIGHT,
  bevelShadow: SHADOW,
  bevelDarkShadow: DARK_SHADOW,
};

/**
 * Draw one bevel ring: `tl` on the top and left edges, `br` on the bottom and
 * right. Bottom-right is painted last so it owns the shared corner pixels,
 * matching how Win95 stacked its border colors.
 */
export function bevelEdge(rc: RenderContext, r: Rect, tl: Color, br: Color): void {
  if (r.width <= 0 || r.height <= 0) return;
  rc.hLine(r.x, r.y, r.width, tl);
  rc.vLine(r.x, r.y, r.height, tl);
  rc.hLine(r.x, r.y + r.height - 1, r.width, br);
  rc.vLine(r.x + r.width - 1, r.y, r.height, br);
}

function shrink(r: Rect, by: number): Rect {
  return { x: r.x + by, y: r.y + by, width: r.width - by * 2, height: r.height - by * 2 };
}

/** The classic two-ring raised button border. */
export function bevelRaised(rc: RenderContext, r: Rect): void {
  bevelEdge(rc, r, HIGHLIGHT, DARK_SHADOW);
  bevelEdge(rc, shrink(r, 1), LIGHT, SHADOW);
}

/** The pressed-in variant: the light and dark rings swap sides. */
export function bevelPressed(rc: RenderContext, r: Rect): void {
  bevelEdge(rc, r, DARK_SHADOW, HIGHLIGHT);
  bevelEdge(rc, shrink(r, 1), SHADOW, LIGHT);
}

/** Sunken well used by text fields, list boxes and scroll tracks. */
export function bevelSunken(rc: RenderContext, r: Rect): void {
  bevelEdge(rc, r, SHADOW, HIGHLIGHT);
  bevelEdge(rc, shrink(r, 1), DARK_SHADOW, LIGHT);
}

/** Single-line groove, as used around group boxes and separators. */
export function bevelGroove(rc: RenderContext, r: Rect): void {
  bevelEdge(rc, r, SHADOW, HIGHLIGHT);
}

/**
 * Windows 95/98 look: square corners, 3D bevels built from 1px edge rects, and
 * a solid gray control face. No hover states — Win95 had none.
 */
export class ClassicTheme extends Theme {
  readonly name = "classic";
  readonly metrics = METRICS;
  readonly palette = PALETTE;
  readonly isDark = false;

  // --- window chrome ----------------------------------------------------

  override windowClientRect(bounds: Rect, s: WindowVisualState): Rect {
    const b = this.frameBorder(s);
    // Frame, then the caption bar inset by 2px, then a 2px gap.
    const top = b + this.captionHeight(s) + 2;
    return {
      x: b,
      y: top,
      width: Math.max(0, bounds.width - b * 2),
      height: Math.max(0, bounds.height - top - b),
    };
  }

  override captionButtonRect(
    bounds: Rect,
    button: CaptionButton,
    s: WindowVisualState,
  ): Rect | null {
    const b = this.frameBorder(s);
    const w = this.metrics.captionButtonWidth;
    const h = this.captionHeight(s) - 4;
    const y = b + 2;
    const right = bounds.width - b - 2;
    switch (button) {
      case "close":
        return s.showClose ? { x: right - w, y, width: w, height: h } : null;
      case "maximize": {
        if (!s.showMaximize) return null;
        // Close sits apart from the min/max pair, as in Win95.
        const offset = (s.showClose ? w + 2 : 0) + w;
        return { x: right - offset, y, width: w, height: h };
      }
      case "minimize": {
        if (!s.showMinimize) return null;
        const offset = (s.showClose ? w + 2 : 0) + (s.showMaximize ? w : 0) + w;
        return { x: right - offset, y, width: w, height: h };
      }
    }
  }

  override drawWindowFrame(rc: RenderContext, bounds: Rect, s: WindowVisualState): void {
    const p = this.palette;
    const r: Rect = { x: 0, y: 0, width: bounds.width, height: bounds.height };

    rc.fillRect(r, FACE);
    bevelRaised(rc, r);

    // A fixed-size window loses the thick sizing frame, so the caption sits
    // flush against the bevel rather than one pixel inside a chunky border.
    const b = this.frameBorder(s);
    const inset = Math.min(1, b);
    const title: Rect = {
      x: b - inset,
      y: b - inset,
      width: r.width - (b - inset) * 2,
      height: this.captionHeight(s) + inset * 2,
    };
    rc.fillRect(title, s.active ? p.titleBarActive : p.titleBarInactive);

    for (const button of ["minimize", "maximize", "close"] as CaptionButton[]) {
      const br = this.captionButtonRect(bounds, button, s);
      if (!br) continue;
      rc.fillRect(br, FACE);
      if (s.pressedButton === button) bevelPressed(rc, br);
      else bevelRaised(rc, br);
      this.#drawGlyph(rc, br, button, s);
    }
  }

  #drawGlyph(rc: RenderContext, r: Rect, button: CaptionButton, s: WindowVisualState): void {
    const ink = BLACK;
    const nudge = s.pressedButton === button ? 1 : 0;
    const cx = Math.round(r.x + r.width / 2) + nudge;
    const cy = Math.round(r.y + r.height / 2) + nudge;
    switch (button) {
      case "minimize":
        rc.fillRect({ x: cx - 3, y: cy + 2, width: 6, height: 2 }, ink);
        break;
      case "maximize":
        if (s.maximized) {
          rc.strokeRect({ x: cx - 2, y: cy - 4, width: 7, height: 6 }, ink);
          rc.fillRect({ x: cx - 2, y: cy - 4, width: 7, height: 2 }, ink);
          rc.fillRect({ x: cx - 5, y: cy - 1, width: 7, height: 6 }, FACE);
          rc.strokeRect({ x: cx - 5, y: cy - 1, width: 7, height: 6 }, ink);
          rc.fillRect({ x: cx - 5, y: cy - 1, width: 7, height: 2 }, ink);
        } else {
          rc.strokeRect({ x: cx - 4, y: cy - 4, width: 9, height: 8 }, ink);
          rc.fillRect({ x: cx - 4, y: cy - 4, width: 9, height: 2 }, ink);
        }
        break;
      case "close":
        for (let i = 0; i < 7; i++) {
          rc.fillRect({ x: cx - 3 + i, y: cy - 3 + i, width: 1, height: 1 }, ink);
          rc.fillRect({ x: cx - 3 + i, y: cy + 3 - i, width: 1, height: 1 }, ink);
          rc.fillRect({ x: cx - 2 + i, y: cy - 3 + i, width: 1, height: 1 }, ink);
          rc.fillRect({ x: cx - 2 + i, y: cy + 3 - i, width: 1, height: 1 }, ink);
        }
        break;
    }
  }

  // --- controls ---------------------------------------------------------

  override drawButton(rc: RenderContext, r: Rect, s: VisualState): void {
    rc.fillRect(r, FACE);
    if (s.isDefault === true) {
      // Default buttons wear an extra black outline and shrink their bevel.
      bevelEdge(rc, r, DARK_SHADOW, DARK_SHADOW);
      const inner = shrink(r, 1);
      if (s.pressed) bevelPressed(rc, inner);
      else bevelRaised(rc, inner);
    } else if (s.pressed) {
      bevelPressed(rc, r);
    } else {
      bevelRaised(rc, r);
    }
    if (s.focused && !s.disabled) this.drawFocusRing(rc, r);
  }

  override buttonTextColor(s: VisualState): Color {
    return s.disabled ? this.palette.textDisabled : this.palette.buttonText;
  }

  override drawTextBox(rc: RenderContext, r: Rect, s: VisualState): void {
    rc.fillRect(r, s.disabled ? FACE : this.palette.fieldBg);
    bevelSunken(rc, r);
  }

  override drawCheckBox(rc: RenderContext, r: Rect, s: VisualState): void {
    rc.fillRect(r, s.disabled ? FACE : s.pressed ? FACE : this.palette.fieldBg);
    bevelSunken(rc, r);
    const ink = s.disabled ? SHADOW : BLACK;
    if (s.indeterminate) {
      // Win95 drew the third state as a gray checker fill.
      rc.fillChecker({ x: r.x + 3, y: r.y + 3, width: r.width - 6, height: r.height - 6 }, SHADOW);
    } else if (s.checked) {
      drawClassicCheck(rc, r, ink);
    }
  }

  override drawRadioButton(rc: RenderContext, r: Rect, s: VisualState): void {
    const radius = Math.min(r.width, r.height) / 2;
    rc.drawRect(r, {
      fill: s.disabled ? FACE : this.palette.fieldBg,
      radius,
      borderWidth: 1,
      borderColor: SHADOW,
    });
    // Two-tone ring approximating the classic sunken circle.
    rc.drawRect(shrink(r, 1), { radius: radius - 1, borderWidth: 1, borderColor: DARK_SHADOW });
    if (s.checked) {
      const dot = Math.max(3, Math.round(r.width * 0.35));
      const inset = (r.width - dot) / 2;
      rc.fillRect(
        { x: r.x + inset, y: r.y + inset, width: dot, height: dot },
        s.disabled ? SHADOW : BLACK,
        dot / 2,
      );
    }
  }

  override drawPanel(rc: RenderContext, r: Rect, sunken: boolean): void {
    rc.fillRect(r, sunken ? this.palette.fieldBg : FACE);
    if (sunken) bevelSunken(rc, r);
    else bevelRaised(rc, r);
  }

  override drawScrollBar(rc: RenderContext, parts: ScrollBarParts, _s: VisualState): void {
    // The track is a 50% checkerboard of face and light gray in the real thing;
    // a flat light gray reads the same at UI sizes and costs one quad.
    rc.fillRect(parts.bounds, this.palette.scrollTrack);

    for (const [part, rect] of [
      ["decrease", parts.decrease],
      ["increase", parts.increase],
    ] as const) {
      if (!rect) continue;
      rc.fillRect(rect, FACE);
      if (parts.pressedPart === part) bevelPressed(rc, rect);
      else bevelRaised(rc, rect);
      drawArrow(rc, rect, arrowDirection(part, parts.vertical), BLACK);
    }

    if (parts.thumb.width > 0 && parts.thumb.height > 0) {
      rc.fillRect(parts.thumb, FACE);
      bevelRaised(rc, parts.thumb);
    }
  }

  override drawListBackground(rc: RenderContext, r: Rect, s: VisualState): void {
    rc.fillRect(r, s.disabled ? FACE : this.palette.fieldBg);
    bevelSunken(rc, r);
  }

  override drawListItem(rc: RenderContext, r: Rect, s: VisualState): void {
    if (!s.selected) return;
    rc.fillRect(r, s.active === false ? this.palette.selectionInactiveBg : this.palette.selectionBg);
  }

  override drawColumnHeader(rc: RenderContext, r: Rect, s: VisualState): void {
    rc.fillRect(r, FACE);
    if (s.pressed) bevelPressed(rc, r);
    else bevelRaised(rc, r);
  }

  /** A Win95 splitter is a raised bevel, wide enough to grab and no more. */
  override drawSplitter(rc: RenderContext, r: Rect, s: SplitterVisualState): void {
    rc.fillRect(r, FACE);
    if (s.dragging) bevelSunken(rc, r);
    else bevelRaised(rc, r);
  }

  /**
   * Win95 had no spinner, so this borrows its progress bar: a ring of hard
   * on/off blocks in the accent color, three lit at a time. No alpha ramp —
   * the era did not have one and it would look wrong next to the bevels.
   */
  override drawSpinner(rc: RenderContext, r: Rect, s: SpinnerVisual): void {
    const count = 8;
    const lit = 3;
    const size = Math.min(r.width, r.height);
    if (size <= 0) return;
    const block = Math.max(2, Math.round(size * 0.2));
    const radius = (size - block) / 2;
    const cx = r.x + r.width / 2;
    const cy = r.y + r.height / 2;
    const head = Math.floor(s.phase * count);

    for (let i = 0; i < count; i++) {
      const behind = (((head - i) % count) + count) % count;
      const on = behind < lit;
      const angle = (i / count) * Math.PI * 2 - Math.PI / 2;
      rc.fillRect(
        {
          x: Math.round(cx + Math.cos(angle) * radius - block / 2),
          y: Math.round(cy + Math.sin(angle) * radius - block / 2),
          width: block,
          height: block,
        },
        on ? (s.disabled ? this.palette.textDisabled : this.palette.accent) : this.palette.bevelShadow,
      );
    }
  }

  /** Win95 tabs: raised bevel, and the selected one stands a little taller. */
  override drawTab(rc: RenderContext, r: Rect, s: TabVisualState): void {
    rc.fillRect(r, this.palette.controlBg);
    const p = this.palette;
    // Light on the top and outer edge, dark on the inner — but never along the
    // edge that meets the page, which is what makes the selected tab merge.
    rc.hLine(r.x + 1, r.y + (s.bottom ? r.height - 1 : 0), r.width - 2, s.bottom ? p.bevelShadow : p.bevelHighlight);
    rc.vLine(r.x, r.y + 1, r.height - 1, p.bevelHighlight);
    rc.vLine(r.x + r.width - 1, r.y + 1, r.height - 1, p.bevelDarkShadow);
    rc.vLine(r.x + r.width - 2, r.y + 2, r.height - 3, p.bevelShadow);
  }

  /** Win95 progress: a sunken well filled with chunky segments. */
  override drawProgressBar(rc: RenderContext, r: Rect, p: ProgressVisual): void {
    bevelSunken(rc, r);
    const inner: Rect = { x: r.x + 2, y: r.y + 2, width: r.width - 4, height: r.height - 4 };
    if (inner.width <= 0 || inner.height <= 0) return;
    rc.fillRect(inner, this.palette.fieldBg);

    const ink = p.disabled ? this.palette.controlBgDisabled : this.palette.accent;
    const block = 8;
    const gap = 2;
    const step = block + gap;
    const total = Math.floor((inner.width + gap) / step);
    if (total <= 0) return;

    if (p.indeterminate) {
      // Three lit blocks marching along the well.
      const lit = 3;
      const head = Math.floor(p.phase * (total + lit)) - lit;
      for (let i = 0; i < lit; i++) {
        const index = head + i;
        if (index < 0 || index >= total) continue;
        rc.fillRect(
          { x: inner.x + index * step, y: inner.y, width: block, height: inner.height },
          ink,
        );
      }
      return;
    }

    const filled = Math.round(total * Math.min(1, Math.max(0, p.fraction)));
    for (let i = 0; i < filled; i++) {
      rc.fillRect({ x: inner.x + i * step, y: inner.y, width: block, height: inner.height }, ink);
    }
  }

  /** The pale-yellow info tip, with a hard black edge. */
  override drawTooltip(rc: RenderContext, r: Rect): void {
    rc.drawRect(r, { fill: TOOLTIP_BG, borderWidth: 1, borderColor: BLACK });
  }

  override tooltipTextColor(): Color {
    return hex("#000000");
  }

  override drawFocusRing(rc: RenderContext, r: Rect): void {
    // Win95's dotted focus rectangle: alternate 1px squares.
    const i = this.metrics.focusRingInset;
    const x0 = Math.round(r.x + i);
    const y0 = Math.round(r.y + i);
    const x1 = Math.round(r.x + r.width - i);
    const y1 = Math.round(r.y + r.height - i);
    const ink = this.palette.focusRing;
    if (x1 <= x0 || y1 <= y0) return;
    rc.fillChecker({ x: x0, y: y0, width: x1 - x0, height: 1 }, ink);
    rc.fillChecker({ x: x0, y: y1 - 1, width: x1 - x0, height: 1 }, ink);
    rc.fillChecker({ x: x0, y: y0, width: 1, height: y1 - y0 }, ink);
    rc.fillChecker({ x: x1 - 1, y: y0, width: 1, height: y1 - y0 }, ink);
  }
}

function arrowDirection(part: "decrease" | "increase", vertical: boolean): "up" | "down" | "left" | "right" {
  if (vertical) return part === "decrease" ? "up" : "down";
  return part === "decrease" ? "left" : "right";
}

/** Solid triangle built from 1px slices — the classic scrollbar arrow. */
export function drawArrow(
  rc: RenderContext,
  box: Rect,
  direction: "up" | "down" | "left" | "right",
  ink: Color,
): void {
  const size = 4;
  const cx = Math.round(box.x + box.width / 2);
  const cy = Math.round(box.y + box.height / 2);
  for (let i = 0; i < size; i++) {
    const span = i * 2 + 1;
    switch (direction) {
      case "up":
        rc.fillRect({ x: cx - i, y: cy - 2 + i, width: span, height: 1 }, ink);
        break;
      case "down":
        rc.fillRect({ x: cx - i, y: cy + 2 - i, width: span, height: 1 }, ink);
        break;
      case "left":
        rc.fillRect({ x: cx - 2 + i, y: cy - i, width: 1, height: span }, ink);
        break;
      case "right":
        rc.fillRect({ x: cx + 2 - i, y: cy - i, width: 1, height: span }, ink);
        break;
    }
  }
}

/** The blocky Win95 checkmark: 1px columns of increasing then decreasing height. */
export function drawClassicCheck(rc: RenderContext, r: Rect, ink: Color): void {
  const x = Math.round(r.x + r.width / 2) - 3;
  const y = Math.round(r.y + r.height / 2) - 3;
  const columns: [number, number][] = [
    [0, 2],
    [1, 3],
    [2, 4],
    [3, 3],
    [4, 2],
    [5, 1],
    [6, 0],
  ];
  for (const [dx, dy] of columns) {
    rc.fillRect({ x: x + dx, y: y + dy, width: 1, height: 3 }, ink);
  }
}
